import {Component, DoCheck, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {WorkflowItemStatus, WorkflowStatus} from '@api/projects/interfaces';
import { IParticipant, IQuestionnaireItem, IQuestionnaireWorkflowItem } from '@interfaces/interfaces';
import {
	SpecialQuestionService,
} from '@workflow/components/questionnaire/questionnaire-item/special-question/special-question.service';
import {BehaviorSubject, Observable, of, Subscription} from 'rxjs';
import {distinctUntilChanged, filter, map, switchMap, tap} from 'rxjs/operators';
import {ProjectGuardService} from '../../../../components/cleanui/system/Guard/project.guard';
import {
	SplitButtonElement,
} from '../../../../components/elements/splitbutton/splitbutton.component';
import {participantPages} from '../../../../pages/participant/participants.routes';
import {
	GroupStatus,
	QuestionStatusService,
} from '../../../../pages/questions/question-status.service';
import {
	ActiveParticipantService,
} from '../../../../services/participants/active-participant.service';
import {ProjectService} from '../../../../services/project/project.service';
import {WorkflowRestService} from '../../../../services/rest/workflow-rest.service';
import {WorkflowItemDirective} from '../../../directives/workflow-item/workflow-item.directive';
import {SessionItemData} from '../../../services/session-item-data';
import {WorkflowCategory, WorkflowService} from '../../../services/workflow.service';
import {MenuService} from '@app/services/menu';
import {
	InterpolationService,
	TextInterpolation,
} from '@app/services/interpolation/interpolation.service';
import {TWorkflowViewMode} from '@workflow/components/navigator/interfaces';
import {WorkflowItemNavigationService} from '@workflow/services/workflow-item-navigation.service';
import {EWorkflowPathKey} from '@app/pages/participant/workflow/workflow-path-key.enum';
import { firstValueFrom } from '@app/util/rxjs-7-compat';
import { flatten } from 'lodash';
import { BlockQuestionItem } from '@interfaces/questions';

@Component({
	selector: 'workflow-questionnaire-overview',
	templateUrl: './questionnaire-overview.component.html',
	styleUrls: ['./questionnaire-overview.component.scss',
		'../../../../workflow/components/navigator/wizard-view/wizard-view.component.scss'],
	providers: [ProjectGuardService],
})
export class QuestionnaireOverviewComponent implements OnInit, OnDestroy, DoCheck {

	workflowItem: IQuestionnaireWorkflowItem;
	workflowCategories: WorkflowCategory[];
	participant: IParticipant;
	itemId: string;
	viewMode: TWorkflowViewMode;
	workflowItemStatus: WorkflowItemStatus;
	allValid: boolean;
	private workflowStatus: WorkflowStatus;
	item: SessionItemData;
	private subscription = new Subscription();
	/**
	 * Observable for the current workflow items SessionItemData
	 */
	readonly item$: BehaviorSubject<SessionItemData> = new BehaviorSubject(undefined);

	isConfirmDialogVisible = false;

	private data: SessionItemData;
	groupStatus: (GroupStatus & {isEditable: boolean }) [] = [];
	unfinishedProcedureGroup: boolean[] = [];

	@ViewChild(WorkflowItemDirective)
	private workflowItemDirective: WorkflowItemDirective;

	public startWorkflowSplitbutton: SplitButtonElement = {
		title: 'workflow.start',
		type: 'primary',
		callback: () => this.continueWorkflow(),
	};
	public continueWorkflowSplitbutton: SplitButtonElement = {
		title: 'workflow.continue',
		type: 'primary',
		callback: () => this.continueWorkflow(),
	};
	public finishWorkflowSplitbutton: SplitButtonElement = {
		title: 'workflow.end',
		type: 'primary',
		icon: 'check',
		callback: () => this.showModalToFinishWorkflow(),
	};
	public cancelWorkflowSplitbutton: SplitButtonElement = {
		title: 'workflow.quitOverview',
		type: 'default',
		icon: 'X',
		callback: () => this.showWorkflow(),
	};

	private participantInfoTemplate: TextInterpolation;
	private participantInfo: string = null;

	constructor(
		projectGuardService: ProjectGuardService,
		private activeParticipantService: ActiveParticipantService,
		private questionStatusService: QuestionStatusService,
		private route: ActivatedRoute,
		private router: Router,
		private workflowService: WorkflowService,
		private workflowItemService: WorkflowItemNavigationService,
		private workflowRestService: WorkflowRestService,
		private projektService: ProjectService,
		private specialQuestionService: SpecialQuestionService,
		private projectService: ProjectService,
		private menuService: MenuService,
		private interpolationService: InterpolationService,
	) { }

	ngOnInit(): void {
		this.subscription.add(this.workflowService.workflow$.pipe(
			switchMap(() => this.paramItemId$),
			switchMap(itemId => this.setItemId(itemId)),
			switchMap(() => this.paramViewMode$),
			tap(viewMode => this.setViewMode(viewMode)),
			switchMap(() => this.activeParticipantService.participant$),
			tap(participant => this.participant = participant),
			filter(participant => !!participant && !!this.projektService.activeProject),
			switchMap(() => this.projectService.participantsConfig$),
			tap(config => {
				const template = config?.details?.info?.name;
				this.participantInfoTemplate = template && this.interpolationService.interpolation(template);
				this.participantInfo = this.participantInfoTemplate?.interpolate(this.participant.data) || this.participant.id;
				this.menuService.title = `${this.participantInfo} - ${this.workflowItem.name}`;
			}),
			switchMap(() => this.getWorkflowStatus$(this.participant)),
			tap(status => {
				this.workflowStatus = status;
				if (this.workflowStatus.status && this.workflowStatus.status[this.itemId]) {
					this.workflowItemStatus = {
						started: this.workflowStatus.status[this.itemId].started,
						completed: this.workflowStatus.status[this.itemId].completed,
					};
				}
			}),
			switchMap(() => {
				if (this.workflowItemStatus.started) {
					return this.workflowRestService.getWorkflowItemStatus$(this.projektService.activeProject.project_id, this.participant.id, this.itemId);
				} else {
					return this.workflowRestService.getWorkflowItemData$(this.projektService.activeProject.project_id, this.participant.id, this.itemId);
				}
			}),
			map(workflowItemData => {
				const completed = workflowItemData[0]?.completed;
				const item = new SessionItemData(workflowItemData[0]?.data);
				item.setProperty('completed', completed);
				return item;
			}),
			tap(item => {
				this.item = item;
				this.finishWorkflowSplitbutton.disabled = !this.item?.data;
			}),
		).subscribe(this.item$));

		this.subscription.add(this.item$.pipe(
			filter(item => !!item),
			switchMap(item => item.data$),
		).subscribe(data => {
			this.allValid = this.getWorkflowItemDataValid(data);
		}));
	}

	ngDoCheck(): void {
		if (this.workflowItemDirective) {
			const items = this.workflowItemDirective.itemFull.data.completed ?
				this.workflowItemDirective.itemFull : this.workflowItemDirective.item;
			if (items) {
				this.data = items;
				this.checkGroupStatus(this.data);
			}
		}
	}

	get finishedAndLocked(): boolean {
		return this.workflowItemStatus?.completed;
	}

	private checkGroupStatus(data: SessionItemData) {
		this.groupStatus = [];
		this.unfinishedProcedureGroup = [];
		for (const group of this.workflowItem.groups) {
			const groupStatus = this.questionStatusService.checkQuestionGroupStatus(group, data);

			const allPreviousGroupsFinished = this.groupStatus.every(
				status => status.status === 'finished' || status.status === 'disabled' || status.isSkippable || status.isSufficient
			);

			this.groupStatus.push({
				...groupStatus,
				isEditable: allPreviousGroupsFinished,
			});
			const isSpecialQuestionOrProcedure = group.items.some(item => item.isProcedure || this.specialQuestionService.isSpecialQuestion(item.type));
			this.unfinishedProcedureGroup.push(isSpecialQuestionOrProcedure && groupStatus.status !== 'finished' && groupStatus.status !== 'disabled');
		}
	}

	private get paramItemId$() : Observable<string> {
		return this.route.paramMap.pipe(
			switchMap(params => of(params.get('itemId'))),
			distinctUntilChanged(),
		);
	}

	private get paramViewMode$() : Observable<TWorkflowViewMode> {
		return this.route.paramMap.pipe(
			switchMap(params => of(params.get('viewMode') as TWorkflowViewMode)),
			distinctUntilChanged(),
		);
	}

	private async setItemId(itemId: string) {
		this.itemId = itemId;

		const workflow = await firstValueFrom(this.workflowService.workflow$);
		
		this.workflowItem = workflow?.items?.find(item => item.id === this.itemId) as IQuestionnaireWorkflowItem;
		
		if (!this.workflowItem) {
			console.warn(`Missing WorkflowItem ${this.itemId}`);
			// TODO: Was tun?
			return;
		}
	}

	private setViewMode(viewMode: TWorkflowViewMode) {
		switch (viewMode) {
			case 'compact':
			case 'wizard':
				this.viewMode = viewMode;
				break;
			default:
				this.viewMode = 'compact';
				break;
		}
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	goToQuestion(pageNumber: number): void {
		if (!this.groupStatus[pageNumber].isEditable && !this.finishedAndLocked) {
			return;
		}

		const link = participantPages.workflowProcessQuestionGroup.base({wfItemId: this.itemId, questionGroupId: pageNumber});
		const completed = this.groupStatus[pageNumber].status === 'finished';

		this.router.navigate([link], { queryParams: { continue: !completed, view: this.viewMode }}).catch(console.error);
	}

	private getWorkflowStatus$(participant: IParticipant): Observable<WorkflowStatus> {
		return this.workflowRestService.getWorkflowStatus$(this.projektService.activeProject.project_id, participant.id);
	}

	private getWorkflowItemDataValid(data: Record<string, unknown>): boolean {
		function isBlockQuestionItem(item:IQuestionnaireItem):item is BlockQuestionItem {
			return item.type === 'BlockQuestion';
		}
		
		const blockQuestionKeys = flatten(
			this.workflowItem.groups.map(group => 
				flatten(group.items.filter(isBlockQuestionItem).map(item => item.items))
			)
		).map(item => item.key);
		
		return blockQuestionKeys.every(key => data[key] === null && !data['completed']);
	}

	continueWorkflow(): void {
		const viewMode = this.viewMode === 'compact' ? 'compact' : undefined;
		this.router.navigate([participantPages.workflowProcess.base({wfItemId: this.workflowItem.id, pageUrl: '/'})], { queryParams: { continue: true, view: viewMode }})
			.catch(console.error);
	}

	showModalToFinishWorkflow(): void {
		this.isConfirmDialogVisible = true;
	}

	handleOkToFinishWorkflow(): void {
		this.workflowService.finishWorkflow(this.participant.id, this.itemId, this.item.data);
		this.isConfirmDialogVisible = false;
	}

	handleCancelToDismissFinishWorkflow(): void {
		this.isConfirmDialogVisible = false;
	}

	showWorkflow(): void {
		this.router.navigate([this.workflowItemService.getFirstAvailableExitURL(this.workflowItem, EWorkflowPathKey.DETAIL)]).catch(console.error);
	}

	quitWizard(): void {
		this.router.navigate([this.workflowItemService.getFirstAvailableExitURL(this.workflowItem, EWorkflowPathKey.OVERVIEW, this.viewMode || 'compact')]).catch(console.error);
	}

}
